import { CustomFieldCustomization, GetPointResponse, ImageType, ImageWithType, IntegrationException } from '../types';
import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { isDefined, isValidString } from '../helpers/utils';
import { revertIconForMarker, setFocusIconForMarker } from '../helpers/markers';

import { StateController } from '../helpers/state/state-management/state-controller';
import { getCookie } from '../helpers/device';
import { getDateTimeStringForPoint } from '../helpers/date';
import { getImageUrls } from '../helpers/components/point-card';
import { ifDefined } from 'lit/directives/if-defined';
import { isUrlValidAndSafe } from '../helpers/strings';
import { parseDescriptionText } from '../helpers/embeds';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('point-card-base')
export class PointCardBase extends LitElement {
    static amplitude_callsite: string = 'Point Card';

    @property({ type: Object })
    point?: GetPointResponse;

    @property(({ type: Array }))
    imageUrls: ImageWithType[] = [];

    @state()
    user_id_cookie = getCookie('user_id');

    private readonly failedImageUrls = new Set<string>();

    /** Failures are recorded from `img.src`, which the browser has resolved and
     *  percent-encoded, while every check comes from the raw stored URL. Both
     *  sides go through here so a name with a space or comma still matches —
     *  otherwise the failure never registers and the card retries forever. */
    private failedKey(url: string) {
        try {
            return new URL(url, window.location.href).href;
        } catch {
            return url;
        }
    }

    protected hasImageFailed(url: string) {
        return this.failedImageUrls.has(this.failedKey(url));
    }

    @state()
    private imageValidationComplete = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private observer?: IntersectionObserver;
    private isIntersecting = false;

    @state()
    private readonly imageValidationPromises = new Map<string, Promise<boolean>>();

    // Track which points have already been validated to avoid re-checking
    private readonly validatedPointIds = new Set<string>();

    connectedCallback() {
        super.connectedCallback();

        this.observer = new IntersectionObserver(([entry]) => {
            this.isIntersecting = entry.isIntersecting;

            if(this.isIntersecting) {
                // Attempt to load images when the card comes into view.
                // The images may already be loaded, but the `loadImages`
                // function will check that.
                this.loadImages(true).catch((error) => {
                    console.error('Error loading images:', error);
                });
            }
        });

        this.observer.observe(this);
      }

    disconnectedCallback() {
        this.observer?.disconnect();
        super.disconnectedCallback();
    }

    protected async updated(changedProperties: Map<string | number | symbol, unknown>) {
        super.updated(changedProperties);

        if (changedProperties.has('point')) {
            // Clear the images when the point changes,
            // so that we attempt not to display incorrect images.
            this.failedImageUrls.clear();
            this.imageUrls = [];

            // Attempt to load images (may not if card is not visible).
            this.loadImages().catch((error) => {
                console.error('Error loading images:', error);
                this.imageValidationComplete = true;
                this.requestUpdate();
            });
        }
    }

    protected async loadImages(newlyIntersecting: boolean = false) {
        // Bail if we do not have a point or topic.
        if (!isDefined(this.point) || !isDefined(topicData.topic)) {
            return;
        }

        // Bail if this card is not in view.
        if (!this.isIntersecting) {
            return;
        }

        // Cache the _current_ point id.
        // We need to do this because this element is sometimes virtualized, and
        // reused for different points.  So, we should check that the point has not
        // changed before we set the images.
        const currentPointId = this.point._id.$oid;

        // Bail if this point has already been validated AND we have valid images.
        // If newly intersecting with no valid images, give it another chance.
        if (this.validatedPointIds.has(currentPointId) && this.imageUrls.length > 0) {
            return;
        }

        // Bail if we already have images (and not newly intersecting).
        if (this.imageUrls.length > 0 && !newlyIntersecting) {
            return;
        }

        // Get the images.
        const imageUrls = await getImageUrls(this.point, topicData.topic, topicData.topicKey, PointCardBase.amplitude_callsite);

        // Bail if the point has changed since we started loading them.
        if (this.point._id.$oid !== currentPointId) {
            return;
        }

        // Reset validation state when starting
        this.imageValidationComplete = false;
        this.imageUrls = [];
        this.failedImageUrls.clear();
        this.imageValidationPromises.clear();

        // Now we can set the images and continue.
        this.imageUrls = imageUrls;

        if ((topicData.hasExceptions === IntegrationException.AustraliaMeatAndLivestock || topicData.hasExceptions === IntegrationException.TeamBuildingAnywhere) && this.point.url) {
            let urlField: CustomFieldCustomization | undefined;
            let team_building_video_embed_url: string | undefined;
            if (topicData.hasExceptions === IntegrationException.TeamBuildingAnywhere) {
                // find custom entry with name 'Video Embed URL'
                urlField = topicData.topic?.add_point_customization?.custom_fields?.find(
                    (field) => field.customizations?.header === 'Video Embed URL'
                );

                team_building_video_embed_url = this.point.custom_information?.custom_field_entries?.find(
                    (entry) => entry.field_id.$oid === urlField?._id?.$oid
                )?.field_value;
            }

            const newInput: ImageWithType = {
                type: ImageType.Embed,
                image: topicData.hasExceptions === IntegrationException.AustraliaMeatAndLivestock ? this.point.url : team_building_video_embed_url ?? '',
            };

            if (newInput.image !== '') {
                this.imageUrls.unshift(newInput);
            }
        }
        this.imageValidationComplete = true;

        // Mark this point as validated so we don't re-check it
        this.validatedPointIds.add(currentPointId);
    }

    // Method to check if image loads without displaying it
    private async isImageValid(imageUrl: string): Promise<boolean> {
        // Use cached result if available
        const cachedPromise = this.imageValidationPromises.get(imageUrl);
        if (cachedPromise) {
            return await cachedPromise;
        }

        const validationPromise = new Promise<boolean>((resolve) => {
            const img = new Image();

            // Set a timeout to avoid hanging on slow/broken images
            const timeout = setTimeout(() => {
                resolve(false);
            }, 1500); // 1.5 second timeout

            img.onload = () => {
                clearTimeout(timeout);
                resolve(true);
            };

            img.onerror = () => {
                clearTimeout(timeout);
                resolve(false);
            };

            // Start loading the image
            img.src = imageUrl;
        });

        // Cache the promise
        this.imageValidationPromises.set(imageUrl, validationPromise);
        return await validationPromise;
    }

    get hasImages() {
        // Don't show images until validation is complete
        if (!this.imageValidationComplete) {
            return false;
        }

        if (!isDefined(this.imageUrls) || this.imageUrls.length === 0) {
            return false;
        }

        // Check if we have any non-embed images that haven't failed
        return this.imageUrls.some(imageUrl =>
            imageUrl.type !== ImageType.Embed &&
            !this.hasImageFailed(imageUrl.image) &&
            isValidString(imageUrl.image)
        );
    }

    /**
     * The image list that should actually be handed to <image-carousel>.
     * `hasImages` already excludes failed / invalid images, but the
     * carousel does no filtering of its own — passing the raw
     * `imageUrls` makes it render a broken image as a black slide.
     * Keep embeds (videos) plus any non-failed, valid-string image.
     */
    get displayableImageUrls(): ImageWithType[] {
        if (!isDefined(this.imageUrls)) {
            return [];
        }
        return this.imageUrls.filter(imageUrl =>
            imageUrl.type === ImageType.Embed ||
            (!this.hasImageFailed(imageUrl.image) && isValidString(imageUrl.image))
        );
    }

    get associatedMarker() {
        return pointCache.getMarker(this.point?._id.$oid ?? '');
    }

    get availableImageCount(): number {
        if (!isDefined(this.imageUrls)) {
            return 0;
        }

        return this.imageUrls.filter(imageUrl =>
            imageUrl.type !== ImageType.Embed &&
            !this.hasImageFailed(imageUrl.image) &&
            isValidString(imageUrl.image)
        ).length;
    }

    get firstImage() {
        if (!this.hasImages) {
            return '';
        }

        for (const imageUrl of this.imageUrls) {
            // Skip embed types and failed images
            if (imageUrl.type !== ImageType.Embed &&
                !this.hasImageFailed(imageUrl.image) &&
                isValidString(imageUrl.image)) {
                return imageUrl.image;
            }
        }
        return '';
    }

    // Method to handle image load errors
    handleImageError(event: Event) {
        const imgElement = event.target as HTMLImageElement;
        this.failedImageUrls.add(this.failedKey(imgElement.src));
        // Trigger re-render to show next available image
        this.requestUpdate();
        // Recording the failure already advanced `firstImage` past it, so swap
        // straight to that rather than stepping forward a second time.
        const nextImage = this.firstImage;
        if (nextImage !== '' && this.failedKey(nextImage) !== this.failedKey(imgElement.src)) {
            imgElement.src = nextImage;
        }
    }

    get pointTags() {
        if (!this.point?.tag_info?.user_tags || topicData.allUserTags.length === 0 || this.point.tag_info.user_tags.length === 0) {
            return [];
        }

        const allUserTags = topicData.allUserTags ?? [];
        const tagOrderMap = new Map(allUserTags.map((tag, index) => [tag._id?.$oid, index]));

        return this.point.tag_info?.user_tags
            .filter((tag, index, self) => isDefined(tag._id) && self.findIndex(t => t._id?.$oid === tag._id?.$oid) === index) // Remove duplicates
            .sort((a, b) => (tagOrderMap.get(a._id?.$oid) ?? Infinity) - (tagOrderMap.get(b._id?.$oid) ?? Infinity));
    }

    get point_id() {
        return this.point?._id.$oid;
    }

    async getEventString(): Promise<string | undefined> {
        if (this.point && topicData.topic) {
            return await getDateTimeStringForPoint(this.point, topicData.topic);
        } else {
            return undefined;
        }
    }

    get descriptionString(): string | undefined {
        if (isValidString(this.point?.description) && isDefined(this.point?.description)) {
            return parseDescriptionText(this.point?.description ?? '', topicData.topic?.open_links_in_same_tab, topicData.topic?.embed_all_description_links);
        }
        return undefined;
    }

    get canShowAdminControls() {
        return topicData.isWriter;
    }

    get creationInfo() {
        const createDate = new Date(this.point?.created_at ?? '');

        return `Point created by ${this.point?.email ?? ''} at ${createDate.toLocaleString()}. ${this.numUpvotes} people have upvoted it`;
    }

    get category() {
        if (!isDefined(this.point?.topic_category_id)) {
            return undefined;
        }
        return topicData.categories.filter(
            (c) => c._id?.$oid === this.point?.topic_category_id.$oid
        )[0];
    }

    get hasThisUserUpvoted() {
        return this.point?.likes?.includes(this.user_id_cookie ?? '') ?? false;
    }

    get numUpvotes() {
        return this.point?.likes?.length ?? 0;
    }

    async onHoverMarker() {
        if (isDefined(topicData.mapComponent) && isDefined(this.associatedMarker) && isDefined(this.point)) {
            setFocusIconForMarker(this.associatedMarker);
        }
    }

    async onExitHoverMarker() {
        if (isDefined(topicData.mapComponent) && isDefined(this.associatedMarker) && isDefined(this.point)) {
            revertIconForMarker(this.associatedMarker);
        }
    }


    get customFieldCardDisplay() {
        let customFormInputs: Array<{ [key: string]: string | TemplateResult }> = [];
        if (this.point?.custom_information?.custom_field_entries) {
            const isUrlButtonField = (f: CustomFieldCustomization) => 'URL' in f.field_type && f.field_type.URL?.show_as_button === true;
            // Admins get a small marker on values inherited from the linked listing.
            const withListingMarker = (value: string | TemplateResult, fromListing?: boolean): string | TemplateResult =>
                fromListing === true && topicData.isWriter
                    ? html`${value} <mwc-icon title="From the listing" style="font-size: 0.9em; vertical-align: text-bottom; opacity: 0.55;">storefront</mwc-icon>`
                    : value;
            customFormInputs = topicData.topic?.add_point_customization?.custom_fields?.filter((f) => !f.archived && f.customizations.hide_on_point_card !== true && !isUrlButtonField(f)).map((field) => {
                const entry = this.point?.custom_information?.custom_field_entries?.find((entry) => entry.field_id.$oid === field._id?.$oid);
                if (isDefined(entry)) {
                    const is_url_type = 'URL' in field.field_type;
                    const is_phone_type = 'Phone' in field.field_type;
                    const is_email_type = 'Email' in field.field_type;
                    if (is_url_type) {
                        const open_in_new_tab = 'URL' in field.field_type ? field.field_type.URL?.open_in_new_tab ?? true : true;
                        return {
                            [field?.customizations.header ?? '']: withListingMarker(html`<a href="${ifDefined(isUrlValidAndSafe(entry.field_value ?? '') ? entry.field_value : '#')}" target="${open_in_new_tab ? '_blank' : '_self'}">Link 🔗</a>`, entry.from_listing)
                        };
                    }
                    else if (is_phone_type) {
                        return {
                            [field?.customizations.header ?? '']: withListingMarker(html`<a href="tel:${entry.field_value ?? ''}">${entry.field_value ?? ''}</a>`, entry.from_listing)
                        };
                    }
                    else if (is_email_type) {
                        return {
                            [field?.customizations.header ?? '']: withListingMarker(html`<a href="mailto:${entry.field_value ?? ''}">${entry.field_value ?? ''}</a>`, entry.from_listing)
                        };
                    }
                    else if ('Rating' in field.field_type) {
                        const maxStars = ('Rating' in field.field_type ? field.field_type.Rating?.maxStars : undefined) ?? 5;
                        const value = parseFloat(entry.field_value ?? '0');
                        return {
                            [field?.customizations.header ?? '']: withListingMarker(this.renderStars(value, maxStars), entry.from_listing)
                        };
                    }
                    return {
                        [field?.customizations.header ?? '']: withListingMarker(entry.field_value ?? '', entry.from_listing)
                    };
                } else {
                    return {};
                }
            }) ?? [];
        };
        return customFormInputs;
    }

    get ratingStarsDisplay(): TemplateResult | string {
        const customFields = topicData.topic?.add_point_customization?.custom_fields;
        if (!customFields || !this.point?.custom_information?.custom_field_entries) {
            return '';
        }
        const ratingField = customFields.find(f => !f.archived && f.customizations.hide_on_point_card !== true && 'Rating' in f.field_type);
        if (!ratingField) {
            return '';
        }
        const entry = this.point.custom_information.custom_field_entries.find(e => e.field_id.$oid === ratingField._id?.$oid);
        if (!entry?.field_value) {
            return '';
        }
        const maxStars = ('Rating' in ratingField.field_type ? ratingField.field_type.Rating?.maxStars : undefined) ?? 5;
        const value = parseFloat(entry.field_value);
        return html`<span style="font-size: 0.85rem;">${this.renderStars(value, maxStars)}</span>`;
    }

    renderStars(value: number, maxStars: number): TemplateResult {
        return html`<span style="display: inline-flex; align-items: center; gap: 4px;">
            <span style="display: inline-flex; line-height: 1;">${Array.from({ length: maxStars }, (_, i) => {
                const isFull = value >= i + 1;
                const isHalf = !isFull && value >= i + 0.5;
                return html`<span style="position: relative; display: inline-block; width: 1em; height: 1em;">
                    <span style="position: absolute; inset: 0; color: #D3D3D3;">★</span>
                    <span style="position: absolute; inset: 0; color: #FFB400; overflow: hidden; width: ${isFull ? '100%' : isHalf ? '50%' : '0%'};">★</span>
                </span>`;
            })}</span>
            <span style="font-size: 0.85em; color: #666;">${value}/${maxStars}</span>
        </span>`;
    }
}
